import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from '../UI/Index';
import { useAuth } from '../../context/AuthContext';
import inventoryService from '../../services/inventoryService';

const RECORD_TYPE_CONFIG = {
  inventory: {
    title: 'Crear nuevo Inventario',
    nameLabel: 'Nombre del Inventario',
    placeholder: "Introduce el nombre del Inventario",
    submitText: 'Crear Inventario',
    contentClassName: '',
  },
  expired: {
    title: 'Crear Caducados',
    nameLabel: 'Nombre del Caducado',
    placeholder: "Introduce el nombre del Caducado",
    submitText: 'Crear Caducado',
    contentClassName: 'bg-orange-50',
  },
};

const CreateInventoryModal = ({ isOpen, onClose, onInventoryCreated, recordType = 'inventory' }) => {
  const cfg = RECORD_TYPE_CONFIG[recordType] ?? RECORD_TYPE_CONFIG.inventory;
  const [formData, setFormData] = useState({
    inventoryName: '',
    startDate: '',
    endDate: '',
    selectedShops: [],
    allShops: false,
    csvFile: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isAdmin } = useAuth();



  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked
      }));

      if (name === 'allShops' && checked) {
        setFormData((prev) => ({
          ...prev,
          selectedShops: []
        }));
      }
      return;
    }

    if (type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0]
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.inventoryName.trim()) {
      setError('El nombre del inventario es requerido');
      return;
    }

    if (isAdmin()) {


      if (!formData.csvFile) {
        setError('El archivo CSV es requerido');
        return;
      }

      if (formData.csvFile.type !== 'text/csv' && !formData.csvFile.name.endsWith('.csv')) {
        setError('El archivo debe ser de tipo CSV');
        return;
      }
    }

    try {
      setLoading(true);

      const inventoryData = {
        inventoryName: formData.inventoryName,
        type: recordType,
      };

      if (isAdmin()) {
        inventoryData.startDate = formData.startDate;
        inventoryData.endDate = formData.endDate;
        inventoryData.csvFile = formData.csvFile;
      }

      const response = await inventoryService.createInventory(inventoryData);

      if (response.success) {
        onInventoryCreated();
      } else {
        setError(response.message || 'Error al crear el inventario');
      }
    } catch (err) {
      setError(err.message || 'Error al crear el inventario');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;

  const oneYearLater = new Date(today);
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
  const yyyyMax = oneYearLater.getFullYear();
  const mmMax = String(oneYearLater.getMonth() + 1).padStart(2, '0');
  const ddMax = String(oneYearLater.getDate()).padStart(2, '0');
  const maxDateStr = `${yyyyMax}-${mmMax}-${ddMax}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={cfg.title}
      placeholder={cfg.placeholder}
      size="lg"
      contentClassName={cfg.contentClassName}
    >
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <form onSubmit={handleSubmit} className="mt-4 space-y-6">
        <div>
          <label htmlFor="inventoryName" className="block text-sm font-medium text-gray-700">
            {cfg.nameLabel} * (Obligatorio)
          </label>
          <input
            placeholder={cfg.placeholder}
            type="text"
            id="inventoryName"
            name="inventoryName"
            value={formData.inventoryName}
            onChange={handleChange}
            required
            maxLength={20}
            className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {isAdmin() && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                Fecha Inicio Inventario * (Obligatorio)
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                min={todayStr}
                max={maxDateStr}

                className="block w-full px-3 py-2 mt-1 text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                Fecha Fin Inventario * (Obligatorio)
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                min={formData.startDate || todayStr}
                max={maxDateStr}

                className="block w-full px-3 py-2 mt-1 text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {isAdmin() && (
          <>
            <div>
              <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700">
                Archivo CSV de Productos * (Obligatorio)
              </label>
              <input
                type="file"
                id="csvFile"
                name="csvFile"
                accept=".csv,text/csv"
                onChange={handleChange}
                required
                className="block w-full px-3 py-2 mt-1 text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                El archivo debe ser un CSV con 2 columnas: tienda y y product_code separado por punto y coma.<br></br><br></br>
                <b>❔ Ejemplo de formato:</b> AE | PARC CENTRAL<b>;</b>8427542110707<br></br><br></br>
                <b>⚠️ AVISO IMPORTANTE:</b> El proceso de creación puede tardar hasta 5 minutos en completarse. <br></br>
                No cierres la ventana para no interrumpir el proceso de creación de inventarios.
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3">
          <Button type="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="primary" buttonType="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creando...
              </>
            ) : (
              cfg.submitText
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateInventoryModal;